import { useState, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { stocksAPI, marketResearchAPI } from '../../services/api';
import {
  ArrowLeft,
  TrendingUp,
  Loader2,
  BarChart3,
  Zap,
  Activity,
  Layers,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  LineChart,
  Percent,
  Building2,
  Flame,
} from 'lucide-react';

// ============================================================================
// CARD WRAPPER
// ============================================================================

interface MarketContextCardProps {
  title: string;
  icon: ReactElement;
  children: ReactElement | null;
  className?: string;
}

function MarketContextCard({ title, icon, children, className = '' }: MarketContextCardProps): ReactElement {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="text-gray-500">{icon}</div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// VISUALIZATION HELPERS
// ============================================================================

interface StackedBarProps {
  segments: Array<{ value: number; pct: number; color: string; label: string }>;
  showLabels?: boolean;
}

function StackedBar({ segments, showLabels = true }: StackedBarProps): ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
        {segments.map((seg, i) => (
          <div
            key={i}
            className="h-full transition-all"
            style={{ width: `${seg.pct}%`, backgroundColor: seg.color }}
            title={`${seg.label}: ${seg.value.toLocaleString()} (${seg.pct.toFixed(1)}%)`}
          />
        ))}
      </div>
      {showLabels && (
        <div className="flex justify-between text-[9px]">
          {segments.map((seg, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: seg.color }} />
              <span className="text-gray-600">{seg.label}</span>
              <span className="text-gray-800 font-mono">{seg.value.toLocaleString()}</span>
              <span className="text-gray-500">({seg.pct.toFixed(1)}%)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ProgressBarProps {
  value: number;
  max: number;
  pct: number;
  label: string;
  color?: string;
}

function ProgressBar({ value, pct, label, color = 'rgb(59, 130, 246)' }: ProgressBarProps): ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-800 font-mono">
          {value.toLocaleString()} <span className="text-gray-500">({pct.toFixed(1)}%)</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

interface MetricRowProps {
  label: string;
  value: string | number;
  subValue?: string;
  color?: string;
}

function MetricRow({ label, value, subValue, color }: MetricRowProps): ReactElement {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`font-mono ${color || 'text-gray-800'}`}>{value}</span>
        {subValue && <span className="text-gray-500">{subValue}</span>}
      </div>
    </div>
  );
}

interface DistributionBarProps {
  buckets: Array<{ label: string; count: number; pct: number; color: string }>;
}

function DistributionBar({ buckets }: DistributionBarProps): ReactElement {
  const maxPct = Math.max(...buckets.map(b => b.pct));
  return (
    <div className="space-y-2">
      {buckets.map((bucket, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] text-gray-600 w-16 truncate">{bucket.label}</span>
          <div className="flex-1 h-4 bg-gray-200 rounded overflow-hidden">
            <div
              className="h-full rounded transition-all flex items-center justify-end px-1"
              style={{
                width: `${(bucket.pct / maxPct) * 100}%`,
                backgroundColor: bucket.color,
                minWidth: bucket.pct > 0 ? '20px' : '0'
              }}
            >
              <span className="text-[9px] font-mono text-white">{bucket.count.toLocaleString()}</span>
            </div>
          </div>
          <span className="text-[10px] text-gray-500 font-mono w-12 text-right">{bucket.pct.toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MarketContext(): ReactElement {
  const [moversType, setMoversType] = useState<'gainers' | 'losers' | 'active'>('gainers');

  const { data: indicesData } = useQuery({
    queryKey: ['market-indices'],
    queryFn: async () => {
      const res = await marketResearchAPI.getIndices<{
        as_of: string;
        market_status: string;
        indices: Array<{
          symbol: string;
          name: string;
          price: number | null;
          change: number | null;
          change_pct: number | null;
        }>;
      }>();
      return res.data;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const marketStatus = query.state.data?.market_status;
      return marketStatus === 'open' ? 60 * 1000 : false;
    },
  });

  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['market-overview'],
    queryFn: async () => {
      const res = await stocksAPI.getMarketOverview();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: sectorsData, isLoading: sectorsLoading } = useQuery({
    queryKey: ['market-sectors'],
    queryFn: async () => {
      const res = await stocksAPI.getSectorPerformance();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: moversData, isLoading: moversLoading } = useQuery({
    queryKey: ['market-movers', moversType],
    queryFn: async () => {
      const res = await stocksAPI.getTopMovers(moversType, 5);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header with back link */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/stocks"
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Market Context</h1>
              {overviewData && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {overviewData.total_stocks.toLocaleString()} stocks • as of {overviewData.as_of_date}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Row 1: Indices + Breadth + Trend */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <MarketContextCard title="Market Indices" icon={<LineChart className="w-4 h-4" />}>
            <div className="space-y-3">
              {indicesData?.indices?.map((idx) => (
                <div key={idx.symbol} className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{idx.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-800">
                      {idx.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '—'}
                    </span>
                    <span className={`text-xs font-mono ${(idx.change_pct || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {idx.change_pct != null ? `${idx.change_pct >= 0 ? '+' : ''}${idx.change_pct.toFixed(2)}%` : '—'}
                    </span>
                  </div>
                </div>
              ))}
              {!indicesData && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          </MarketContextCard>

          <MarketContextCard title="Market Breadth" icon={<ArrowUpDown className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData ? (
              <div className="space-y-4">
                <StackedBar
                  segments={[
                    { value: overviewData.breadth.advancing, pct: overviewData.breadth.pct_advancing, color: 'rgb(16, 185, 129)', label: '▲ Adv' },
                    { value: overviewData.breadth.unchanged, pct: overviewData.breadth.pct_unchanged, color: 'rgb(100, 116, 139)', label: '─ Unch' },
                    { value: overviewData.breadth.declining, pct: overviewData.breadth.pct_declining, color: 'rgb(239, 68, 68)', label: '▼ Dec' },
                  ]}
                />
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200">
                  <MetricRow
                    label="A/D Ratio"
                    value={overviewData.breadth.ad_ratio?.toFixed(2) || '—'}
                    color={(overviewData.breadth.ad_ratio || 0) >= 1 ? 'text-emerald-600' : 'text-red-600'}
                  />
                  <MetricRow
                    label="New Highs"
                    value={overviewData.breadth.new_52w_highs}
                    color="text-emerald-600"
                  />
                  <MetricRow
                    label="New Lows"
                    value={overviewData.breadth.new_52w_lows}
                    color="text-red-600"
                  />
                </div>
              </div>
            ) : null}
          </MarketContextCard>

          <MarketContextCard title="Trend & MA" icon={<TrendingUp className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData ? (
              <div className="space-y-3">
                <ProgressBar
                  value={overviewData.trend.above_sma20}
                  max={overviewData.total_stocks}
                  pct={overviewData.trend.pct_above_sma20}
                  label="Above SMA20"
                  color="rgb(59, 130, 246)"
                />
                <ProgressBar
                  value={overviewData.trend.above_sma50}
                  max={overviewData.total_stocks}
                  pct={overviewData.trend.pct_above_sma50}
                  label="Above SMA50"
                  color="rgb(99, 102, 241)"
                />
                <ProgressBar
                  value={overviewData.trend.above_sma200}
                  max={overviewData.total_stocks}
                  pct={overviewData.trend.pct_above_sma200}
                  label="Above SMA200"
                  color="rgb(139, 92, 246)"
                />
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-[10px] text-gray-500">Golden Cross</span>
                    <span className="text-[10px] font-mono text-emerald-600">{overviewData.trend.golden_cross}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-[10px] text-gray-500">Death Cross</span>
                    <span className="text-[10px] font-mono text-red-600">{overviewData.trend.death_cross}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </MarketContextCard>
        </div>

        {/* Row 2: Momentum + Volatility + Performance */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <MarketContextCard title="Momentum" icon={<Activity className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-500 mb-1">RSI Distribution</div>
                  <StackedBar
                    segments={[
                      { value: overviewData.momentum.rsi_oversold, pct: overviewData.momentum.pct_rsi_oversold, color: 'rgb(16, 185, 129)', label: '<30' },
                      { value: overviewData.momentum.rsi_neutral, pct: overviewData.momentum.pct_rsi_neutral, color: 'rgb(100, 116, 139)', label: '30-70' },
                      { value: overviewData.momentum.rsi_overbought, pct: overviewData.momentum.pct_rsi_overbought, color: 'rgb(239, 68, 68)', label: '>70' },
                    ]}
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-500 mb-1">MACD Signal</div>
                  <StackedBar
                    segments={[
                      { value: overviewData.momentum.macd_bullish, pct: overviewData.momentum.pct_macd_bullish, color: 'rgb(16, 185, 129)', label: 'Bullish' },
                      { value: overviewData.momentum.macd_bearish, pct: overviewData.momentum.pct_macd_bearish, color: 'rgb(239, 68, 68)', label: 'Bearish' },
                    ]}
                  />
                </div>
                <MetricRow label="Avg RSI" value={overviewData.momentum.avg_rsi?.toFixed(1) || '—'} />
              </div>
            ) : null}
          </MarketContextCard>

          <MarketContextCard title="Volatility & Risk" icon={<Zap className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData ? (
              <div className="space-y-3">
                <DistributionBar
                  buckets={[
                    { label: 'Low (<15%)', count: overviewData.volatility.vol_low, pct: overviewData.volatility.pct_vol_low, color: 'rgb(16, 185, 129)' },
                    { label: 'Med (15-30%)', count: overviewData.volatility.vol_medium, pct: overviewData.volatility.pct_vol_medium, color: 'rgb(251, 191, 36)' },
                    { label: 'High (>30%)', count: overviewData.volatility.vol_high, pct: overviewData.volatility.pct_vol_high, color: 'rgb(239, 68, 68)' },
                  ]}
                />
                <div className="pt-2 border-t border-gray-200 grid grid-cols-2 gap-2">
                  <MetricRow label="Avg Vol" value={`${overviewData.volatility.avg_volatility_20d?.toFixed(1)}%`} />
                  <MetricRow label="Avg DD YTD" value={`${overviewData.volatility.avg_drawdown_ytd?.toFixed(1)}%`} color="text-red-600" />
                </div>
              </div>
            ) : null}
          </MarketContextCard>

          <MarketContextCard title="vs S&P 500" icon={<Percent className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData?.relative_performance ? (
              <div className="space-y-2">
                {overviewData.relative_performance.slice(0, 4).map((perf) => (
                  <div key={perf.timeframe} className="space-y-1">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-gray-600 font-medium">{perf.timeframe}</span>
                      <span className="text-gray-500">
                        Beating: <span className="text-emerald-600 font-mono">{perf.pct_outperforming.toFixed(0)}%</span>
                      </span>
                    </div>
                    <StackedBar
                      segments={[
                        { value: perf.outperforming, pct: perf.pct_outperforming, color: 'rgb(16, 185, 129)', label: '' },
                        { value: perf.underperforming, pct: perf.pct_underperforming, color: 'rgb(239, 68, 68)', label: '' },
                      ]}
                      showLabels={false}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </MarketContextCard>
        </div>

        {/* Row 3: Volume + Sectors + Movers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MarketContextCard title="Volume & Activity" icon={<BarChart3 className="w-4 h-4" />}>
            {overviewLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : overviewData ? (
              <div className="space-y-3">
                <DistributionBar
                  buckets={[
                    { label: 'Low (<0.5x)', count: overviewData.volume.rvol_low, pct: overviewData.volume.pct_rvol_low, color: 'rgb(100, 116, 139)' },
                    { label: 'Normal', count: overviewData.volume.rvol_normal, pct: overviewData.volume.pct_rvol_normal, color: 'rgb(59, 130, 246)' },
                    { label: 'High (1.5-3x)', count: overviewData.volume.rvol_high, pct: overviewData.volume.pct_rvol_high, color: 'rgb(251, 191, 36)' },
                    { label: 'Unusual (>3x)', count: overviewData.volume.rvol_unusual, pct: overviewData.volume.pct_rvol_unusual, color: 'rgb(239, 68, 68)' },
                  ]}
                />
                <div className="pt-2 border-t border-gray-200 flex justify-between">
                  <div className="flex items-center gap-1">
                    <ArrowUp className="w-3 h-3 text-emerald-500" />
                    <span className="text-[10px] text-gray-500">Gap Up</span>
                    <span className="text-[10px] font-mono text-emerald-600">{overviewData.volume.gap_up}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ArrowDown className="w-3 h-3 text-red-500" />
                    <span className="text-[10px] text-gray-500">Gap Down</span>
                    <span className="text-[10px] font-mono text-red-600">{overviewData.volume.gap_down}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </MarketContextCard>

          <MarketContextCard title="Sector Heatmap" icon={<Building2 className="w-4 h-4" />}>
            {sectorsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : sectorsData?.sectors && sectorsData.sectors.length > 0 ? (
              <div className="h-52">
                <ReactECharts
                  option={{
                    tooltip: {
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      borderColor: '#e5e7eb',
                      textStyle: { color: '#1f2937' },
                      formatter: (info: { name: string; data: { change: number } }) => {
                        const pct = info.data?.change ?? 0;
                        const color = pct >= 0 ? '#059669' : '#dc2626';
                        return `<div style="padding:4px 8px"><div style="font-weight:600;margin-bottom:4px">${info.name}</div><div style="font-family:monospace;font-size:14px;font-weight:700;color:${color}">${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%</div></div>`;
                      },
                    },
                    series: [{
                      type: 'treemap',
                      left: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                      roam: false,
                      nodeClick: false,
                      breadcrumb: { show: false },
                      squareRatio: 1,
                      label: {
                        show: true,
                        formatter: (params: { name: string; data: { change: number } }) => {
                          const pct = params.data?.change ?? 0;
                          return `{name|${params.name}}\n{pct|${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%}`;
                        },
                        rich: {
                          name: { fontSize: 10, color: '#ffffff', fontWeight: 500, lineHeight: 14 },
                          pct: { fontSize: 12, fontFamily: 'monospace', fontWeight: 700, lineHeight: 16, color: '#ffffff' },
                        },
                        padding: [4, 4, 4, 4],
                      },
                      itemStyle: {
                        borderColor: '#ffffff',
                        borderWidth: 1,
                        gapWidth: 1,
                        borderRadius: 3,
                      },
                      emphasis: {
                        itemStyle: { borderColor: '#374151', borderWidth: 2 },
                        label: { rich: { name: { color: '#ffffff' } } },
                      },
                      data: sectorsData.sectors.map(s => {
                        const pct = s.change_pct ?? 0;
                        const intensity = Math.min(Math.abs(pct) / 2, 1);
                        const color = pct >= 0
                          ? `rgb(${Math.round(22 + 30 * intensity)}, ${Math.round(128 + 50 * intensity)}, ${Math.round(85 + 40 * intensity)})`
                          : `rgb(${Math.round(185 + 40 * intensity)}, ${Math.round(60 + 20 * intensity)}, ${Math.round(60 + 20 * intensity)})`;
                        return {
                          name: s.sector,
                          value: 100,
                          change: pct,
                          itemStyle: { color },
                          label: { rich: { pct: { color: '#ffffff' } } },
                        };
                      }),
                    }],
                  }}
                  style={{ height: '100%', width: '100%' }}
                  opts={{ renderer: 'svg' }}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No data</p>
              </div>
            )}
          </MarketContextCard>

          <MarketContextCard title="Top Movers" icon={<Flame className="w-4 h-4" />}>
            <div className="space-y-3">
              <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
                {(['gainers', 'losers', 'active'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setMoversType(type)}
                    className={`flex-1 px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                      moversType === type
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {type === 'gainers' ? 'Gainers' : type === 'losers' ? 'Losers' : 'Active'}
                  </button>
                ))}
              </div>
              {moversLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : moversData?.movers ? (
                <div className="space-y-1.5">
                  {moversData.movers.map((stock) => (
                    <Link
                      key={stock.symbol}
                      to={`/stocks/${stock.symbol}`}
                      className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono font-semibold text-amber-600 group-hover:text-amber-700">
                          {stock.symbol}
                        </span>
                        <span className="text-[10px] text-gray-500 truncate max-w-[80px]">
                          {stock.name?.split(' ').slice(0, 2).join(' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-700">
                          ${stock.price?.toFixed(2)}
                        </span>
                        <span className={`text-xs font-mono font-medium ${
                          moversType === 'active'
                            ? 'text-cyan-600'
                            : (stock.change_pct || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {moversType === 'active'
                            ? `${stock.relative_volume?.toFixed(1)}x`
                            : `${(stock.change_pct || 0) >= 0 ? '+' : ''}${stock.change_pct?.toFixed(2)}%`
                          }
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-xs">No movers data</div>
              )}
            </div>
          </MarketContextCard>
        </div>
      </div>
    </div>
  );
}
